package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.dto.owner.SharedCostAllocationDto
import com.evoapps.evoshift.data.dto.owner.SharedCostDto
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.AllocationBasis
import com.evoapps.evoshift.domain.model.AllocationMethod
import com.evoapps.evoshift.domain.model.InvalidSharedCostException
import com.evoapps.evoshift.domain.model.SharedCost
import com.evoapps.evoshift.domain.model.SharedCostAllocation
import com.evoapps.evoshift.domain.model.SharedCostFrequency
import com.evoapps.evoshift.domain.repository.SharedCostRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject
import kotlin.coroutines.cancellation.CancellationException

/**
 * SH-131: the owner reads owners/{uid}/shared_costs directly; saving and deleting are the
 * saveSharedCost / deleteSharedCost callables (functions/sharedCosts.js), which validate the split
 * and the workplaces and write the parts. A refusal with `details.reason` naming a
 * [SharedCostAllocation.Problem] comes back as [InvalidSharedCostException].
 */
class SharedCostRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : SharedCostRepository {

    override suspend fun getSharedCosts(ownerId: String): Result<List<SharedCost>> = guarded {
        firestore.collection("owners").document(ownerId).collection("shared_costs").get()
            .documents.map { toDomain(it.data<SharedCostDto>().copy(id = it.id)) }
            .sortedBy { it.name.lowercase() }
    }

    override suspend fun saveSharedCost(cost: SharedCost, replacesRuleId: String?): Result<String> = guarded {
        val result = functions.call("saveSharedCost", buildJsonObject {
            if (cost.id.isNotBlank()) put("id", cost.id)
            put("name", cost.name)
            put("amount", cost.amount)
            put("currency", cost.currency)
            put("frequency", cost.frequency.name)
            cost.date?.let { put("date", it) }
            cost.startMonth?.let { put("startMonth", it) }
            cost.endMonth?.let { put("endMonth", it) }
            putJsonObject("allocation") {
                put("method", cost.method.name)
                cost.basis?.let { put("basis", it.name) }
                putJsonArray("workplaceIds") { cost.workplaceIds.forEach { add(kotlinx.serialization.json.JsonPrimitive(it)) } }
                putJsonObject("shares") { cost.shares.forEach { (id, value) -> put(id, value) } }
            }
            replacesRuleId?.let { put("replacesRuleId", it) }
        })
        ((result as? JsonObject)?.get("id")?.jsonPrimitive?.contentOrNull) ?: cost.id
    }

    override suspend fun deleteSharedCost(costId: String): Result<Unit> = guarded {
        functions.call("deleteSharedCost", buildJsonObject { put("id", costId) })
        Unit
    }

    private inline fun <T> guarded(block: () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        val reason = ((e.details as? JsonObject)?.get("reason"))?.jsonPrimitive?.contentOrNull
        val problem = SharedCostAllocation.Problem.entries.firstOrNull { it.name == reason }
        Result.failure(if (problem != null) InvalidSharedCostException(setOf(problem)) else e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    companion object {
        fun toDomain(dto: SharedCostDto): SharedCost = SharedCost(
            id = dto.id,
            name = dto.name,
            amount = dto.amount,
            currency = dto.currency,
            frequency = SharedCostFrequency.entries.firstOrNull { it.name == dto.frequency } ?: SharedCostFrequency.MONTHLY,
            date = dto.date,
            startMonth = dto.startMonth,
            endMonth = dto.endMonth,
            method = AllocationMethod.entries.firstOrNull { it.name == dto.allocation.method } ?: AllocationMethod.PROPORTIONAL,
            basis = AllocationBasis.entries.firstOrNull { it.name == dto.allocation.basis },
            workplaceIds = dto.allocation.workplaceIds,
            shares = dto.allocation.shares
        )

        fun toDto(domain: SharedCost): SharedCostDto = SharedCostDto(
            id = domain.id,
            name = domain.name,
            amount = domain.amount,
            currency = domain.currency,
            frequency = domain.frequency.name,
            date = domain.date,
            startMonth = domain.startMonth,
            endMonth = domain.endMonth,
            allocation = SharedCostAllocationDto(domain.method.name, domain.basis?.name, domain.workplaceIds, domain.shares)
        )
    }
}
